import React, { useState } from 'react';
import { Box, Button, TextField, Typography, Paper } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { HolidayService } from '../../services/holiday.service';
import { Holiday } from '../../interfaces/holiday.interface';
import { format } from 'date-fns';
import { useToast } from '../../context/ToastContext';

const HolidayForm: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const existingHoliday = location.state?.holiday as Holiday | undefined;

    const [formData, setFormData] = useState({
        name: existingHoliday?.name || '',
        startDate: existingHoliday ? format(new Date(existingHoliday.startDate), 'yyyy-MM-dd') : '',
        endDate: existingHoliday ? format(new Date(existingHoliday.endDate), 'yyyy-MM-dd') : '',
        description: existingHoliday?.description || ''
    });
    const toast = useToast();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validate dates
        if (new Date(formData.endDate) < new Date(formData.startDate)) {
            toast.showError('End date cannot be before start date');
            return;
        }

        try {
            if (existingHoliday) {
                await HolidayService.updateHoliday(existingHoliday.id, formData);
            } else {
                await HolidayService.createHoliday(formData);
            }
            navigate('/holidays');
        } catch (err) {
            toast.showApiError(err, 'Failed to save holiday');
            console.error(err);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    return (
        <Box data-help-page="holidays.form.page" sx={{ padding: 3 }}>
            <Paper sx={{ padding: 3, maxWidth: 600, margin: '0 auto' }}>
                <Typography variant="h5" sx={{ marginBottom: 3 }}>
                    {existingHoliday ? 'Edit Holiday' : 'Create Holiday'}
                </Typography>
                <form data-help-section="holidays.form.section.details" onSubmit={handleSubmit}>
                    <TextField
                        fullWidth
                        data-help="holidays.form.field.name" label="Name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        sx={{ marginBottom: 2 }}
                    />
                    <TextField
                        fullWidth
                        data-help="holidays.form.field.startDate" label="Start Date"
                        name="startDate"
                        type="date"
                        value={formData.startDate}
                        onChange={handleChange}
                        required
                        InputLabelProps={{ shrink: true }}
                        sx={{ marginBottom: 2 }}
                    />
                    <TextField
                        fullWidth
                        data-help="holidays.form.field.endDate" label="End Date"
                        name="endDate"
                        type="date"
                        value={formData.endDate}
                        onChange={handleChange}
                        required
                        InputLabelProps={{ shrink: true }}
                        sx={{ marginBottom: 2 }}
                    />
                    <TextField
                        fullWidth
                        data-help="holidays.form.field.description" label="Description"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        multiline
                        rows={4}
                        sx={{ marginBottom: 3 }}
                    />
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button
                            data-help="holidays.form.save" type="submit"
                            variant="contained"
                            color="primary"
                        >
                            {existingHoliday ? 'Update' : 'Create'}
                        </Button>
                        <Button
                            variant="outlined"
                            data-help="holidays.form.cancel" onClick={() => navigate('/holidays')}
                        >
                            Cancel
                        </Button>
                    </Box>
                </form>
            </Paper>
        </Box>
    );
};

export default HolidayForm;
